<template>
  <div class="flex flex-col gap-1">
    <h3 class="text-sm font-medium text-muted-foreground py-1">{{ $t(label) }}</h3>
    <div class="relative">
      <!-- Scrolls, with a visible scrollbar, when taller than the panel allows (`--mega-menu-max-height`, set by
           MainMenuFlyout) minus the panel's padding and this heading; no scrollbar otherwise. -->
      <ul
        ref="list"
        class="flex flex-col gap-2 overflow-y-auto overflow-x-hidden scrollbar-visible"
        style="max-height: calc(var(--mega-menu-max-height, 65vh) - 5rem)"
        @scroll="updateScrollShadows">
        <li v-for="entry in entries" :key="entry.label">
          <RouterLink
            :to="entry.to || '/'"
            class="flex items-center gap-3 min-h-11 pr-3 rounded-lg text-sm font-semibold transition-[padding,background-color] duration-200 ease-out hover:pl-3 hover:bg-surface-selected"
            :class="entry.label === activeLabel ? 'pl-3 bg-surface-selected' : 'pl-0'"
            @mouseenter="hoverEntry(entry)"
            @mouseleave="cancelHover"
            @focus="emit('activate', entry)"
            @click="emit('navigate')">
            <CountryFlag v-if="entry.flag" :code="entry.flag" small />
            <component :is="entryIcon(entry)" v-else class="size-5 shrink-0" />
            <span class="whitespace-nowrap">{{ $t(entry.label) }}</span>
            <!-- The entry's groups open beside the list. -->
            <ChevronRightIcon class="size-5 shrink-0 ms-auto text-muted-foreground" aria-hidden="true" />
          </RouterLink>
        </li>
      </ul>
      <!-- Scroll shadows, above the entries so a hovered or selected entry doesn't cover them: shown at the top / bottom
           while there is more to scroll that way. -->
      <div
        aria-hidden="true"
        class="scroll-shadow-top pointer-events-none absolute inset-x-0 top-0 h-4 transition-opacity duration-150"
        :class="canScrollUp ? 'opacity-100' : 'opacity-0'" />
      <div
        aria-hidden="true"
        class="scroll-shadow-bottom pointer-events-none absolute inset-x-0 bottom-0 h-4 transition-opacity duration-150"
        :class="canScrollDown ? 'opacity-100' : 'opacity-0'" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useResizeObserver } from "@vueuse/core";
import CountryFlag from "@/components/CountryFlag.vue";
import { ChevronRightIcon } from "@xs2event/js-lib/icons";
import { entryIcon } from "./useMegaMenu";

// Moving the pointer diagonally towards the entry's groups crosses other entries; only switch once it rests.
const HOVER_DELAY_MS = 150;

type Entry = { label: string; to?: string; flag?: string; params?: { sport_type?: string } };

const props = defineProps<{ label: string; entries: Entry[]; activeLabel?: string }>();
const emit = defineEmits<{ activate: [entry: Entry]; navigate: [] }>();

const list = ref<HTMLElement>();
const canScrollUp = ref(false);
const canScrollDown = ref(false);

const updateScrollShadows = () => {
  if (!list.value) return;
  const { scrollTop, clientHeight, scrollHeight } = list.value;
  canScrollUp.value = scrollTop > 0;
  // 1px slack for fractional heights.
  canScrollDown.value = scrollTop + clientHeight < scrollHeight - 1;
};

// The list's height (panel or window resize) and its entries both change what is left to scroll.
useResizeObserver(list, updateScrollShadows);
watch(
  () => props.entries,
  () => nextTick(updateScrollShadows)
);

let hoverTimeout: ReturnType<typeof setTimeout> | undefined;

const hoverEntry = (entry: Entry) => {
  clearTimeout(hoverTimeout);
  hoverTimeout = setTimeout(() => emit("activate", entry), HOVER_DELAY_MS);
};

const cancelHover = () => clearTimeout(hoverTimeout);

onBeforeUnmount(cancelHover);
</script>
